import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Customer } from './types';

interface CustomersListProps {
    customers: Customer[];
    onAddToRoute: (customer: Customer) => void;
}

export const CustomersList = ({ customers, onAddToRoute }: CustomersListProps) => {
    const { t } = useTranslation() as { t: (key: string) => string };
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [importanceLevelFilter, setImportanceLevelFilter] = useState<string>('');

    const filteredCustomers = customers.filter(customer => {
        const matchesSearch = customer.name.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = importanceLevelFilter === '' || customer.importance_level === importanceLevelFilter;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-full flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">{t('common.label.lista-customers')}</h3>
                <div className="relative">
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t('common.button.search')}
                        className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <svg
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                    </svg>
                </div>
                
                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        {t('common.label.categoria')}
                    </label>
                    <div className="flex gap-6">
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="importance_level_filter"
                                value=""
                                checked={importanceLevelFilter === ''}
                                onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                            />
                            <span className="ml-2 text-sm text-gray-700">{t('common.label.todas')}</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="importance_level_filter"
                                value="A"
                                checked={importanceLevelFilter === 'A'}
                                onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                            />
                            <span className="ml-2 text-sm text-gray-700">A</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="importance_level_filter"
                                value="B"
                                checked={importanceLevelFilter === 'B'}
                                onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                            />
                            <span className="ml-2 text-sm text-gray-700">B</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                            <input
                                type="radio"
                                name="importance_level_filter"
                                value="C"
                                checked={importanceLevelFilter === 'C'}
                                onChange={(e) => setImportanceLevelFilter(e.target.value)}
                                className="w-4 h-4 text-[#006bc6] border-gray-300 focus:ring-[#006bc6]"
                            />
                            <span className="ml-2 text-sm text-gray-700">C</span>
                        </label>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {filteredCustomers.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                        <svg
                            className="mx-auto h-12 w-12 text-gray-400 mb-3"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                            />
                        </svg>
                        <p className="text-sm">
                            {searchTerm ? t('common.label.no-results') : t('common.label.no-customers')}
                        </p>
                    </div>
                ) : (
                    filteredCustomers.map((customer) => (
                        <div
                            key={customer.id}
                            className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-all bg-white  cursor-pointer"
                            style={{
                                borderLeftWidth: '4px',
                                borderLeftColor: customer.is_acquired ? '#10b981' : '#f59e0b'
                            }}
                            //onClick={() => onEdit(customer)}
                        >
                            <div className="flex items-start justify-between mb-2">
                                <h4 className="font-semibold text-gray-800 flex-1 break-words">{customer.name}</h4>
                                <div
                                    className={`w-3 h-3 rounded-full flex-shrink-0 ml-2 ${customer.is_acquired ? 'bg-green-500' : 'bg-yellow-500'
                                        }`}
                                />
                            </div>

                            {customer.address && (
                                <p className="text-xs text-gray-600 mb-2 line-clamp-2">{customer.address}</p>
                            )}

                            <p className="text-xs text-gray-600 mb-2 line-clamp-2"><span className="font-semibold">{t('common.label.categoria')} </span>{customer.importance_level}</p>

                            {/* {customer.lat && customer.lon && (
                                <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                    <span className="flex items-center gap-1">
                                        <svg
                                            className="w-3 h-3"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                            />
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                            />
                                        </svg>
                                        {customer.lat.toFixed(4)}, {customer.lon.toFixed(4)}
                                    </span>
                                </div>
                            )} */}

                            <div className="flex items-center justify-between">
                                <span
                                    className={`text-xs font-semibold px-2 py-1 rounded ${customer.is_acquired
                                            ? 'bg-green-100 text-green-800'
                                            : 'bg-yellow-100 text-yellow-800'
                                        }`}
                                >
                                    {customer.is_acquired
                                        ? `✓ ${t('common.form-label.cliente')}`
                                        : `○ ${t('common.label.prospecto')}`
                                    }
                                </span>

                                <div className="flex gap-2">
                                    <button
                                        onClick={() => onAddToRoute(customer)}
                                        className="p-1.5 text-sm text-[#006bc6] hover:bg-blue-50 rounded transition-all"
                                        title={t('common.label.anadir-a-la-ruta')}
                                    >
                                       {t('common.label.anadir-a-la-ruta')}
                                    </button>
                                </div> 
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};
